import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { isDefined, isNotDefined } from '@sgme/fp';
import { Dropdown } from '@sgme/ui';
import { useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { BACKGROUND_LIGHT_COLOR } from '@/App/tiles/CashTile/constants.ts';
import { NdfCheckbox } from '@/App/tiles/orders/components/NdfCheckbox.tsx';
import ToggleProduct from '@/App/tiles/ToggleProduct.tsx';
import { ChangeClientDropDownItem } from '@/components/ClientSelector/ChangeClientDropDownItem.tsx';
import { TileOverridenClientIndicator } from '@/components/ClientSelector/TileOverridenClientIndicator.tsx';
import CurrencyPairPicker from '@/components/ui/CurrencyPairPicker/index.tsx';
import { useAppDispatch } from '@/store/hooks.ts';
import type { AlgoOrder } from '@/store/orders/algoOrders/state.ts';
import type { CallOrder } from '@/store/orders/call/state.ts';
import type { FixingOrder } from '@/store/orders/fixing/state.ts';
import { ordersSlice } from '@/store/orders/slice.ts';
import { isAlgoOrder, type Order, type OrderType, type ValidateOrderResponse } from '@/store/orders/state.ts';
import type { StopLossOrder } from '@/store/orders/stopLoss/state.ts';
import type { TakeProfitOrder } from '@/store/orders/takeProfitOrder/state.ts';
import { removeTile } from '@/store/tiles/slice.ts';

type OrderHeaderProps = {
  tile: Order | undefined;
  tileId: string;
  orderType: OrderType;
  validate: (
    updatedProperties:
      | Partial<StopLossOrder['properties']>
      | Partial<TakeProfitOrder['properties']>
      | Partial<CallOrder['properties']>
      | Partial<FixingOrder['properties']>
      | Partial<AlgoOrder['properties']>,
  ) => Promise<
    | {
        data: ValidateOrderResponse;
        error: undefined;
      }
    | {
        data: undefined;
        error: FetchBaseQueryError | SerializedError;
      }
    | undefined
  >;
};

export const OrderHeader = ({ tile, tileId, validate, orderType }: OrderHeaderProps) => {
  const currencyPair = isDefined(tile?.properties.currencyPair)
    ? `${tile?.properties.currencyPair.currency1}/${tile?.properties.currencyPair.currency2}`
    : undefined;

  const clientId = tile?.clientId;

  const dispatch = useAppDispatch();

  const dropdownToggleRef = useRef<HTMLButtonElement>(null);

  const handleCurrencyPairChange = async (newCurrencyPair: string) => {
    const [currency1, currency2] = newCurrencyPair.split('/');

    if (isNotDefined(tile) || isNotDefined(currency1) || isNotDefined(currency2)) {
      console.error('Invalid currency pair:', newCurrencyPair);
      return;
    }

    const response = await validate({
      currencyPair: {
        currency1,
        currency2,
      },
    });

    if (tile.type === 'FixingOrder' && isDefined(response?.data)) {
      const fixing = response.data;

      await validate({
        fixingReference: fixing.fixingTypes.length > 0 ? fixing.fixingTypes[0] : undefined,
        currencyPair: {
          currency1,
          currency2,
        },
      });
    }
  };

  const isAlgoOrderType = isAlgoOrder(tile?.type);
  const isNdf = isAlgoOrderType ? tile.properties.isNdf : false;

  const handleClose = () => {
    dispatch(removeTile(tileId));
  };

  const handleSelectClient = (clientId: number) => {
    dispatch(ordersSlice.actions.changeClientId({ tileId, clientId }));
  };

  const handleClearSelectedClient = () => {
    dispatch(ordersSlice.actions.changeClientId({ tileId, clientId: undefined }));
  };

  return (
    <div>
      <TileOverridenClientIndicator clientId={clientId} onRemoveOverride={handleClearSelectedClient} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center' }} className="mb-2">
        <div>
          <ToggleProduct tileId={tileId} />
        </div>

        <CurrencyPairPicker
          tradeType="Order"
          orderType={orderType}
          value={currencyPair}
          onChange={handleCurrencyPairChange}
          data-e2e="currency-pair-input"
        />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
          {isAlgoOrderType && <NdfCheckbox isNdf={isNdf} />}

          <Dropdown color={BACKGROUND_LIGHT_COLOR} direction="auto" size="md" data-e2e="tile-menu">
            <Dropdown.Toggle className="btn-icon" data-e2e="tileMenu-btn">
              <i className="icon" ref={dropdownToggleRef}>
                more_vert
              </i>
            </Dropdown.Toggle>

            <Dropdown.Menu>
              <ChangeClientDropDownItem triggerRef={dropdownToggleRef} onSelectClient={handleSelectClient} />

              <Dropdown.Item onClick={handleClose} data-e2e="close-tile-menu">
                <FormattedMessage id="order.header.menu.close" />
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    </div>
  );
};
